import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, IconButton, Paper, CircularProgress, Avatar, InputAdornment, Divider, Chip, Stack, Alert, Snackbar, useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import QuizIcon from '@mui/icons-material/Quiz';
import skillsService from '../services/skills.service';
import AddTestDrawer from './AddTestDrawer';
import TestDetailDrawer from './TestDetailDrawer';

const TestManagement = () => {
    const theme = useTheme();
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
    const [isDetailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchTests();
    }, []);

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        if (nameParts.length > 1) {
            return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        } else if (nameParts[0] && nameParts[0].length > 1) {
            return nameParts[0].substring(0, 2).toUpperCase();
        }
        return (name[0] || '').toUpperCase();
    };

    const fetchTests = () => {
        setLoading(true);
        // Add cache busting timestamp to force fresh data
        skillsService.getTests().then(response => {
            const freshTests = response.data.data || response.data || [];
            setTests(freshTests);
            if (selectedTest) {
                const updatedTest = freshTests.find(t => t.id === selectedTest.id);
                setSelectedTest(updatedTest || null);
            }
        }).catch(error => {
            console.error("Error fetching tests", error);
        }).finally(() => setLoading(false));
    };

    const handleTestCreated = () => {
        fetchTests();
        setAddDrawerOpen(false);
    };

    const handleTestUpdated = () => {
        fetchTests();
        // Refresh selected test data
        if (selectedTest) {
            skillsService.getTest(selectedTest.id).then(response => {
                setSelectedTest(response.data);
            }).catch(error => {
                console.error("Error fetching updated test details:", error);
            });
        }
    };

    const handleDeleteTest = (id) => {
        skillsService.deleteTest(id).then(() => {
            fetchTests();
            setDetailDrawerOpen(false);
            setSelectedTest(null);
            setSnackbar({ open: true, message: 'Test bol úspešne vymazaný.', severity: 'success' });
        }).catch(error => {
            console.error("Error deleting test", error);
            const errorMessage = error.response?.data?.message || error.message || 'Nepodarilo sa vymazať test.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        });
    };

    const handleOpenDetailDrawer = (test) => {
        // Fetch full test with questions
        skillsService.getTest(test.id).then(response => {
            setSelectedTest(response.data);
            setDetailDrawerOpen(true);
        }).catch(error => {
            console.error("Error fetching test details", error);
        });
    };

    const handleCloseDetailDrawer = () => {
        setDetailDrawerOpen(false);
        setSelectedTest(null);
    };

    const filteredTests = tests.filter(test =>
        test.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTestTypeLabel = (type) => {
        return type === 'technical' ? 'Technical' : 'Soft Skills';
    };

    const getTotalTime = (test) => {
        if (!test.time_per_question || !test.questions_count) return null;
        const totalSeconds = test.time_per_question * test.questions_count;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    };

    if (loading) {
        return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
    }

    return (
        <Box sx={{ p: 3, margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Tests</Typography>
                <IconButton
                    onClick={() => setAddDrawerOpen(true)}
                    size="large"
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        boxShadow: 3,
                        '&:hover': {
                            bgcolor: 'primary.dark',
                        },
                    }}
                >
                    <AddIcon fontSize="large" />
                </IconButton>
            </Box>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        backgroundColor: 'background.paper',
                        transition: 'box-shadow 200ms ease-in-out',
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.divider,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: `1px solid ${theme.palette.primary.main}`,
                        },
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: 3,
                }}
            >
                {filteredTests.length > 0 ? filteredTests.map(test => (
                    <Paper
                        key={test.id}
                        onClick={() => handleOpenDetailDrawer(test)}
                        sx={{
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            borderRadius: 4,
                            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
                            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                            cursor: 'pointer',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px rgba(0,0,0,0.1)',
                            },
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, flexGrow: 1 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 50, height: 50, fontSize: '1.2rem', fontWeight: 'bold' }}>
                                <QuizIcon />
                            </Avatar>
                            <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {test.name}
                                </Typography>
                                {test.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {test.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ px: 2, pb: 2 }}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={getTestTypeLabel(test.test_type)}
                                        color={test.test_type === 'technical' ? 'primary' : 'secondary'}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={test.is_active ? 'Active' : 'Inactive'}
                                        color={test.is_active ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Questions: {test.questions_count || 0}
                                </Typography>
                                {getTotalTime(test) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Estimated time: {getTotalTime(test)}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Paper>
                )) : (
                    <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', gridColumn: '1 / -1' }}>
                        No tests found.
                    </Typography>
                )}
            </Box>

            <AddTestDrawer
                open={isAddDrawerOpen}
                onClose={() => setAddDrawerOpen(false)}
                onTestCreated={handleTestCreated}
            />

            <TestDetailDrawer
                test={selectedTest}
                open={isDetailDrawerOpen}
                onClose={handleCloseDetailDrawer}
                onTestDeleted={handleDeleteTest}
                onTestUpdated={handleTestUpdated}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TestManagement;




